import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  createGame as fbCreateGame, 
  joinGame as fbJoinGame, 
  startGame as fbStartGame,
  removeCard as fbRemoveCard,
  undoRemovedCard as fbUndoRemovedCard,
  subscribeToGame,
  resetGame as fbResetGame,
  db
} from '../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { 
  generateGameId, 
  generatePlayerId, 
  distributeCards,
  removeRandomCard,
  shuffleDeck,
  generateDeck
} from '../utils/cardUtils';
import { getGameType } from '../utils/gameTypes';
import { initializeUnoGame, canPlayCard, handleActionCard, drawCard as drawUnoCardUtil } from '../utils/unoGameLogic';
import {
  initializeExplodingKittensGame,
  drawCard as drawKittensCardUtil,
  placeExplodingKitten as placeKittenUtil,
  handleActionCard as handleKittensActionCard,
  hasDefuseCard,
  removeDefuseCard
} from '../utils/explodingKittensLogic';
import {
  playUnoCard as fbPlayUnoCard,
  drawUnoCard as fbDrawUnoCard,
  callUno as fbCallUno,
  setWildCardColor as fbSetWildCardColor,
  playKittensCard as fbPlayKittensCard,
  drawKittensCard as fbDrawKittensCard,
  placeExplodingKitten as fbPlaceExplodingKitten
} from '../services/firebase';

// Create context
const GameContext = createContext();

// Store last removed card for undo
let lastRemovedCard = null;
let lastRemovedPlayerId = null;

// Sound effect for card removal
const playCardSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch (e) {
    console.log('Sound not supported');
  }
};

// Provider component
export const GameProvider = ({ children }) => {
  const [game, setGame] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session on page reload
  useEffect(() => {
    const savedGameId = sessionStorage.getItem('gameId');
    const savedPlayerId = sessionStorage.getItem('playerId');
    
    if (savedGameId && savedPlayerId) {
      // Restore the game session
      setGame({ id: savedGameId });
      setCurrentPlayer({ id: savedPlayerId });
    } else {
      setLoading(false);
    }
  }, []);

  // Subscribe to game updates when game exists
  useEffect(() => {
    if (!game?.id) return;
    
    const unsubscribe = subscribeToGame(game.id, (gameData) => {
      if (gameData) {
        setGame(gameData);
        // Restore full currentPlayer data from game state
        const savedPlayerId = sessionStorage.getItem('playerId');
        if (savedPlayerId && gameData.players) {
          const playerData = gameData.players.find(p => p.id === savedPlayerId);
          if (playerData) {
            setCurrentPlayer(playerData);
          }
        }
      } else {
        // Game no longer exists, clear session
        sessionStorage.removeItem('playerId');
        sessionStorage.removeItem('gameId');
        setGame(null);
        setCurrentPlayer(null);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [game?.id]);

  // Create a new game
  const createNewGame = useCallback(async (playerName, gameTypeId = 'billiards') => {
    setLoading(true);
    setError(null);
    
    try {
      const gameId = generateGameId();
      const playerId = generatePlayerId();
      
      const player = {
        id: playerId,
        name: playerName,
        cards: [],
        isWinner: false,
        isHost: true
      };
      
      const gameData = await fbCreateGame(gameId, player, gameTypeId);
      setGame(gameData);
      setCurrentPlayer(player);
      
      // Store player info in session
      sessionStorage.setItem('playerId', playerId);
      sessionStorage.setItem('gameId', gameId);
      
      return gameId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Join an existing game
  const joinExistingGame = useCallback(async (gameId, playerName) => {
    setLoading(true);
    setError(null);
    
    try {
      const playerId = generatePlayerId();
      
      const player = {
        id: playerId,
        name: playerName,
        cards: [],
        isWinner: false,
        isHost: false
      };
      
      await fbJoinGame(gameId.toUpperCase(), player);
      
      // Subscribe to get the updated game data
      setCurrentPlayer(player);
      setGame({ id: gameId.toUpperCase() }); // Trigger subscription
      
      // Store player info in session
      sessionStorage.setItem('playerId', playerId);
      sessionStorage.setItem('gameId', gameId.toUpperCase());
      
      return gameId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Start the game (host only)
  const startTheGame = useCallback(async (cardsPerPlayer = null, allowDuplicates = null) => {
    if (!game || !currentPlayer?.isHost) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const gameTypeId = game.gameType || 'billiards';
      const gameType = getGameType(gameTypeId);
      
      const defaultCardsPerPlayer = cardsPerPlayer || gameType.defaultCardsPerPlayer;
      const defaultAllowDuplicates = allowDuplicates !== null ? allowDuplicates : gameType.defaultAllowDuplicates;
      
      let unoGameState = null;
      
      const distributedCards = distributeCards(game.players.length, defaultCardsPerPlayer, gameTypeId, defaultAllowDuplicates);
      
      let kittensGameState = null;
      
      // For UNO, we need to set up the discard and draw piles
      if (gameTypeId === 'uno') {
        const fullDeck = generateDeck(gameTypeId, defaultAllowDuplicates, gameType.defaultCopies);
        const shuffledDeck = shuffleDeck(fullDeck);
        unoGameState = initializeUnoGame(shuffledDeck, distributedCards);
      }
      
      // For Exploding Kittens, set up draw pile with exploding kittens
      if (gameTypeId === 'explodingKittens') {
        const fullDeck = generateDeck(gameTypeId, defaultAllowDuplicates, gameType.defaultCopies);
        const shuffledDeck = shuffleDeck(fullDeck);
        kittensGameState = initializeExplodingKittensGame(shuffledDeck, distributedCards, game.players.length);
      }
      
      await fbStartGame(game.id, game.players, distributedCards, gameTypeId, unoGameState, kittensGameState);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  // Remove a ball - removes from ALL players who have that ball number
  const potBall = useCallback(async (cardToRemove = null) => {
    if (!game || !currentPlayer) return;
    
    // Find current player in game state
    const playerInGame = game.players.find(p => p.id === currentPlayer.id);
    if (!playerInGame || playerInGame.cards.length === 0) return;
    
    setLoading(true);
    
    try {
      let removedCard;
      
      if (cardToRemove) {
        removedCard = cardToRemove;
      } else {
        // Remove random card (fallback)
        const result = removeRandomCard(playerInGame.cards);
        removedCard = result.removedCard;
      }
      
      // Get the identifier to match cards (number for most games, or id for exact match)
      const cardIdentifier = removedCard.id;
      
      // Store for undo - track all affected players
      lastRemovedCard = removedCard;
      lastRemovedPlayerId = currentPlayer.id;
      
      // Update ALL players - remove cards matching the removed card's identifier
      // For billiards: match by number
      // For other games: match by id for exact card removal
      const gameTypeId = game.gameType || 'billiards';
      const updatedPlayers = game.players.map(p => {
        let remainingCards;
        if (gameTypeId === 'billiards') {
          // Billiards: remove all cards with same number
          remainingCards = p.cards.filter(c => c.number !== removedCard.number);
        } else {
          // Other games: remove cards with matching id (exact card)
          remainingCards = p.cards.filter(c => c.id !== cardIdentifier);
        }
        return {
          ...p,
          cards: remainingCards,
          isWinner: remainingCards.length === 0
        };
      });
      
      // Check for winner (first player with 0 cards)
      const winner = updatedPlayers.find(p => p.isWinner);
      const winnerId = winner ? winner.id : null;
      
      // Play sound effect
      playCardSound();
      
      // Create removed card entry for history
      const removedCardEntry = {
        card: removedCard,
        cardNumber: removedCard.number,
        cardId: cardIdentifier,
        playerName: playerInGame.name,
        playerId: currentPlayer.id,
        timestamp: Date.now()
      };
      
      await fbRemoveCard(game.id, currentPlayer.id, updatedPlayers, winnerId, removedCardEntry);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  // Undo last removed card
  const undoRemove = useCallback(async () => {
    if (!game || !currentPlayer || !lastRemovedCard || lastRemovedPlayerId !== currentPlayer.id) return;
    
    const playerInGame = game.players.find(p => p.id === currentPlayer.id);
    if (!playerInGame) return;
    
    setLoading(true);
    
    try {
      // Add the card back
      const updatedCards = [...playerInGame.cards, lastRemovedCard];
      
      const updatedPlayers = game.players.map(p => {
        if (p.id === currentPlayer.id) {
          return {
            ...p,
            cards: updatedCards,
            isWinner: false
          };
        }
        return p;
      });
      
      // Remove last entry from history
      const updatedRemovedCards = (game.removedCards || []).slice(0, -1);
      
      await fbUndoRemovedCard(game.id, updatedPlayers, updatedRemovedCards);
      
      // Clear undo state
      lastRemovedCard = null;
      lastRemovedPlayerId = null;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  // Check if undo is available
  const canUndo = useCallback(() => {
    return lastRemovedCard !== null && lastRemovedPlayerId === currentPlayer?.id;
  }, [currentPlayer]);

  // Reset game
  const resetGame = useCallback(async () => {
    if (!game) return;
    
    try {
      await fbResetGame(game.id);
    } catch (err) {
      setError(err.message);
    }
  }, [game]);

  // Leave game
  const leaveGame = useCallback(() => {
    setGame(null);
    setCurrentPlayer(null);
    sessionStorage.removeItem('playerId');
    sessionStorage.removeItem('gameId');
  }, []);

  // Get current player's data from game state
  const getCurrentPlayerData = useCallback(() => {
    if (!game || !currentPlayer) return null;
    return game.players.find(p => p.id === currentPlayer.id);
  }, [game, currentPlayer]);

  // UNO-specific actions
  const playUnoCard = useCallback(async (cardIndex, wildColor = null) => {
    if (!game || !currentPlayer) return;
    
    const gameTypeId = game.gameType || 'billiards';
    if (gameTypeId !== 'uno') return;
    
    const playerInGame = game.players.find(p => p.id === currentPlayer.id);
    if (!playerInGame || !playerInGame.cards || playerInGame.cards.length === 0) return;
    
    // Check if it's this player's turn
    const currentPlayerIndex = game.currentPlayerIndex !== undefined ? game.currentPlayerIndex : 0;
    const playerIndex = game.players.findIndex(p => p.id === currentPlayer.id);
    if (playerIndex !== currentPlayerIndex) {
      setError('Not your turn!');
      return;
    }
    
    // Check for pending draw cards
    if (game.pendingDrawCards > 0) {
      setError('You must draw cards first!');
      return;
    }
    
    // Check for pending wild color selection
    if (game.pendingWildColor !== null && !wildColor) {
      setError('Please select a color for the wild card');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const card = playerInGame.cards[cardIndex];
      if (!card) {
        setError('Invalid card');
        return;
      }
      
      const topCard = game.discardPile && game.discardPile.length > 0 
        ? game.discardPile[game.discardPile.length - 1] 
        : null;
      
      // Check if card can be played - wild cards are always allowed
      const isWildCard = card.type === 'wild' || card.action === 'wild' || card.action === 'wild_draw4';
      if (!isWildCard && !canPlayCard(card, topCard)) {
        setError('Cannot play this card! It must match the color, number, or symbol of the top card.');
        setLoading(false);
        return;
      }
      
      // Handle wild card color
      let cardToPlay = card;
      if ((card.type === 'wild' || card.action === 'wild' || card.action === 'wild_draw4') && wildColor) {
        cardToPlay = { ...card, colorName: wildColor };
        const UNO_COLORS = {
          red: '#DC2626',
          blue: '#2563EB',
          green: '#16A34A',
          yellow: '#FACC15',
        };
        cardToPlay.color = UNO_COLORS[wildColor] || card.color;
      }
      
      // Remove card from player's hand
      const updatedCards = [...playerInGame.cards];
      updatedCards.splice(cardIndex, 1);
      
      // Check if player won
      const isWinner = updatedCards.length === 0;
      
      // Update discard pile
      const discardPile = [...(game.discardPile || [])];
      discardPile.push(cardToPlay);
      
      // Handle action cards
      const direction = game.direction || 1;
      const playerCount = game.players.length;
      const { nextPlayerIndex, newDirection, drawCards, skipTurn } = handleActionCard(
        cardToPlay,
        currentPlayerIndex,
        direction,
        playerCount
      );
      
      // Update players
      const updatedPlayers = game.players.map((p, idx) => {
        if (p.id === currentPlayer.id) {
          return {
            ...p,
            cards: updatedCards,
            isWinner: isWinner,
            hasCalledUno: false
          };
        }
        // Apply draw cards to next player
        if (drawCards > 0 && idx === nextPlayerIndex && !isWinner) {
          const drawPile = game.drawPile || [];
          const newCards = [];
          let remainingDrawPile = [...drawPile];
          let updatedDiscardPile = [...discardPile];
          
          for (let i = 0; i < drawCards; i++) {
            const drawResult = drawUnoCardUtil(remainingDrawPile, updatedDiscardPile);
            if (drawResult.card) {
              newCards.push(drawResult.card);
              remainingDrawPile = drawResult.newDrawPile;
              updatedDiscardPile = drawResult.newDiscardPile;
            } else {
              break; // No more cards available
            }
          }
          
          // Update discard pile in case it was reshuffled
          discardPile.length = 0;
          discardPile.push(...updatedDiscardPile);
          
          return {
            ...p,
            cards: [...(p.cards || []), ...newCards]
          };
        }
        return p;
      });
      
      // Update draw pile - use the remaining draw pile from the draw operations
      let drawPile = game.drawPile || [];
      if (drawCards > 0) {
        // The draw pile was already updated in the loop above
        // We need to track it properly - for now, calculate remaining
        let tempDrawPile = [...drawPile];
        let tempDiscardPile = [...discardPile];
        for (let i = 0; i < drawCards; i++) {
          const result = drawUnoCardUtil(tempDrawPile, tempDiscardPile);
          if (result.card) {
            tempDrawPile = result.newDrawPile;
            tempDiscardPile = result.newDiscardPile;
          } else {
            break;
          }
        }
        drawPile = tempDrawPile;
      }
      
      const winnerId = isWinner ? currentPlayer.id : null;
      
      playCardSound();
      
      await fbPlayUnoCard(
        game.id,
        currentPlayer.id,
        cardIndex,
        updatedPlayers,
        discardPile,
        drawPile,
        nextPlayerIndex,
        newDirection,
        0, // pendingDrawCards cleared
        null, // pendingWildColor cleared
        winnerId
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  const drawUnoCard = useCallback(async () => {
    if (!game || !currentPlayer) return;
    
    const gameTypeId = game.gameType || 'billiards';
    if (gameTypeId !== 'uno') return;
    
    const currentPlayerIndex = game.currentPlayerIndex !== undefined ? game.currentPlayerIndex : 0;
    const playerIndex = game.players.findIndex(p => p.id === currentPlayer.id);
    if (playerIndex !== currentPlayerIndex) {
      setError('Not your turn!');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const drawPile = game.drawPile || [];
      const discardPile = game.discardPile || [];
      const result = drawUnoCardUtil(drawPile, discardPile);
      
      if (!result.card) {
        setError('No cards available to draw');
        setLoading(false);
        return;
      }
      
      const playerInGame = game.players.find(p => p.id === currentPlayer.id);
      const updatedPlayers = game.players.map(p => {
        if (p.id === currentPlayer.id) {
          return {
            ...p,
            cards: [...(p.cards || []), result.card]
          };
        }
        return p;
      });
      
      const direction = game.direction || 1;
      const nextPlayerIndex = (currentPlayerIndex + direction + game.players.length) % game.players.length;
      
      await fbDrawUnoCard(
        game.id,
        updatedPlayers,
        result.newDrawPile,
        nextPlayerIndex
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  const callUno = useCallback(async () => {
    if (!game || !currentPlayer) return;
    
    const playerInGame = game.players.find(p => p.id === currentPlayer.id);
    if (!playerInGame || playerInGame.cards.length !== 1) {
      setError('You can only call UNO when you have 1 card!');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const updatedPlayers = game.players.map(p => {
        if (p.id === currentPlayer.id) {
          return {
            ...p,
            hasCalledUno: true
          };
        }
        return p;
      });
      
      await fbCallUno(game.id, currentPlayer.id, updatedPlayers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  // Exploding Kittens-specific actions
  const playKittensCard = useCallback(async (cardIndices) => {
    if (!game || !currentPlayer) return;
    
    const gameTypeId = game.gameType || 'billiards';
    if (gameTypeId !== 'explodingKittens') return;
    
    const playerInGame = game.players.find(p => p.id === currentPlayer.id);
    if (!playerInGame || !playerInGame.cards || playerInGame.cards.length === 0) return;
    if (!playerInGame.isAlive) return;
    
    // Check if it's this player's turn
    const currentPlayerIndex = game.currentPlayerIndex !== undefined ? game.currentPlayerIndex : 0;
    const playerIndex = game.players.findIndex(p => p.id === currentPlayer.id);
    if (playerIndex !== currentPlayerIndex) {
      setError('Not your turn!');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const selectedCards = cardIndices.map(idx => playerInGame.cards[idx]).filter(Boolean);
      if (selectedCards.length === 0) {
        setError('No cards selected');
        setLoading(false);
        return;
      }
      
      // Remove cards from player's hand
      const updatedCards = [...playerInGame.cards];
      cardIndices.sort((a, b) => b - a); // Sort descending to remove from end
      cardIndices.forEach(idx => updatedCards.splice(idx, 1));
      
      // Handle action cards
      const direction = game.direction || 1;
      const playerCount = game.players.length;
      let nextPlayerIndex = currentPlayerIndex;
      let skipDraw = false;
      let attackActive = false;
      
      // Process first action card
      if (selectedCards.length > 0 && selectedCards[0].type === 'action') {
        const result = handleKittensActionCard(selectedCards[0], currentPlayerIndex, playerCount);
        nextPlayerIndex = result.nextPlayerIndex;
        skipDraw = result.skipDraw;
        attackActive = result.attackActive;
      } else {
        // Non-action cards - advance turn normally
        nextPlayerIndex = (currentPlayerIndex + 1) % playerCount;
      }
      
      const updatedPlayers = game.players.map(p => {
        if (p.id === currentPlayer.id) {
          return {
            ...p,
            cards: updatedCards
          };
        }
        return p;
      });
      
      playCardSound();
      
      await fbPlayKittensCard(
        game.id,
        currentPlayer.id,
        cardIndices,
        updatedPlayers,
        game.drawPile || [],
        nextPlayerIndex,
        skipDraw,
        attackActive
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  const drawKittensCard = useCallback(async () => {
    if (!game || !currentPlayer) return;
    
    const gameTypeId = game.gameType || 'billiards';
    if (gameTypeId !== 'explodingKittens') return;
    
    const currentPlayerIndex = game.currentPlayerIndex !== undefined ? game.currentPlayerIndex : 0;
    const playerIndex = game.players.findIndex(p => p.id === currentPlayer.id);
    if (playerIndex !== currentPlayerIndex) {
      setError('Not your turn!');
      return;
    }
    
    if (game.skipDraw) {
      setError('You skipped drawing this turn!');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const drawPile = game.drawPile || [];
      const result = drawKittensCardUtil(drawPile);
      
      if (!result.card) {
        setError('No cards available to draw');
        setLoading(false);
        return;
      }
      
      const playerInGame = game.players.find(p => p.id === currentPlayer.id);
      
      // If it's an exploding kitten
      if (result.isExplodingKitten) {
        // Check if player has defuse card
        if (hasDefuseCard(playerInGame.cards)) {
          // Player can defuse - mark as pending
          const { newHand } = removeDefuseCard(playerInGame.cards);
          const updatedPlayers = game.players.map(p => {
            if (p.id === currentPlayer.id) {
              return {
                ...p,
                cards: newHand
              };
            }
            return p;
          });
          
          // Store the exploding kitten card for placement
          // Pass the full card object so it can be placed back
          await fbDrawKittensCard(
            game.id,
            updatedPlayers,
            result.newDrawPile,
            currentPlayerIndex,
            result.card, // Store the full exploding kitten card object
            null,
            true // Player is alive
          );
        } else {
          // Player dies
          const updatedPlayers = game.players.map(p => {
            if (p.id === currentPlayer.id) {
              return {
                ...p,
                isAlive: false,
                cards: [] // Remove all cards
              };
            }
            return p;
          });
          
          // Check if only one player is alive (winner)
          const alivePlayers = updatedPlayers.filter(p => p.isAlive);
          let winnerId = null;
          if (alivePlayers.length === 1) {
            winnerId = alivePlayers[0].id;
            // Mark winner
            updatedPlayers.forEach(p => {
              if (p.id === winnerId) {
                p.isWinner = true;
              }
            });
          }
          
          const nextPlayerIndex = (currentPlayerIndex + 1) % game.players.length;
          
          await fbDrawKittensCard(
            game.id,
            updatedPlayers,
            result.newDrawPile,
            nextPlayerIndex,
            null,
            winnerId,
            false // Player died
          );
        }
      } else {
        // Regular card - add to hand
        const updatedPlayers = game.players.map(p => {
          if (p.id === currentPlayer.id) {
            return {
              ...p,
              cards: [...(p.cards || []), result.card]
            };
          }
          return p;
        });
        
        const nextPlayerIndex = (currentPlayerIndex + 1) % game.players.length;
        
        await fbDrawKittensCard(
          game.id,
          updatedPlayers,
          result.newDrawPile,
          nextPlayerIndex,
          null,
          null,
          true
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  const placeExplodingKitten = useCallback(async (position) => {
    if (!game || !currentPlayer) return;
    
    const gameTypeId = game.gameType || 'billiards';
    if (gameTypeId !== 'explodingKittens') return;
    
    if (!game.pendingDefuse) {
      setError('No exploding kitten to place');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // pendingDefuse is the exploding kitten card object
      const explodingKitten = typeof game.pendingDefuse === 'string' 
        ? { id: game.pendingDefuse, type: 'exploding_kitten' }
        : game.pendingDefuse;
      
      const drawPile = game.drawPile || [];
      const newDrawPile = placeKittenUtil(drawPile, explodingKitten, position);
      
      const nextPlayerIndex = (game.currentPlayerIndex + 1) % game.players.length;
      
      const GAMES_COLLECTION = 'billiards_games';
      const gameRef = doc(db, GAMES_COLLECTION, game.id);
      await updateDoc(gameRef, {
        drawPile: newDrawPile,
        pendingDefuse: null,
        currentPlayerIndex: nextPlayerIndex
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, currentPlayer]);

  const value = {
    game,
    currentPlayer,
    loading,
    error,
    createNewGame,
    joinExistingGame,
    startTheGame,
    potBall,
    undoRemove,
    canUndo,
    resetGame,
    leaveGame,
    getCurrentPlayerData,
    isHost: currentPlayer?.isHost || false,
    // UNO-specific functions
    playUnoCard,
    drawUnoCard,
    callUno,
    // Exploding Kittens-specific functions
    playKittensCard,
    drawKittensCard,
    placeExplodingKitten
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

// Custom hook to use game context
export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

